/**
 * JSON Schema property -> field component.
 *
 * The whole per-field decision lives here, in priority order. Getting this
 * ordering right is what makes a manifest author's intent win: an explicit
 * `x-lookup` or `x-ui.widget` should always beat what the raw type implies.
 */

import type { ReactNode } from 'react'
import type { JsonSchema } from '@/core/types/api.types'
import { hints, schemaType } from '@/core/utils/schema.utils'
import {
  BooleanField,
  DateField,
  JsonField,
  MultiSelectField,
  NumberField,
  SelectField,
  TextField,
} from './fields/primitives'
import { ArrayField, ObjectField } from './fields/composites'
import { DynamicSelectField } from './fields/DynamicSelectField'

export interface ResolveArgs {
  name: string
  path: string
  schema: JsonSchema
  value: unknown
  onChange: (value: unknown) => void
  error?: string
  required?: boolean
  disabled?: boolean
  connectionId: string | null
  formValues: Record<string, unknown>
  /** Field key -> label, so a gated dropdown can name its dependency. */
  siblingLabels?: Record<string, string>
  renderChild: (
    args: Omit<ResolveArgs, 'connectionId' | 'formValues' | 'siblingLabels' | 'renderChild'>,
  ) => ReactNode
}

export function resolveField(args: ResolveArgs): ReactNode {
  const { schema, connectionId, formValues, siblingLabels, renderChild, path, ...rest } = args
  const base = { ...rest, schema }
  const ui = hints(schema)
  const type = schemaType(schema)

  // 1. A live lookup is the strongest signal there is.
  const lookup = schema['x-lookup']
  if (lookup) {
    return (
      <DynamicSelectField
        {...base}
        lookup={lookup}
        connectionId={connectionId}
        formValues={formValues}
        siblingLabels={siblingLabels}
      />
    )
  }

  // 2. An explicit widget override.
  if (ui.widget === 'json') return <JsonField {...base} />

  // 3. A closed set of values.
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return <SelectField {...base} />
  }
  if (type === 'array' && Array.isArray(schema.items?.enum)) {
    return <MultiSelectField {...base} />
  }

  // 4. Structure.
  if (type === 'array') {
    return <ArrayField {...base} path={path} renderChild={renderChild} />
  }
  if (type === 'object') {
    // An object with no declared properties can only be edited as raw JSON —
    // there is nothing to build a sub-form from.
    if (!schema.properties || Object.keys(schema.properties).length === 0) {
      return <JsonField {...base} />
    }
    return <ObjectField {...base} path={path} renderChild={renderChild} />
  }

  // 5. Scalars.
  if (type === 'boolean') return <BooleanField {...base} />
  if (type === 'number' || type === 'integer') return <NumberField {...base} />
  if (schema.format === 'date' || schema.format === 'date-time') {
    return <DateField {...base} />
  }

  return <TextField {...base} />
}
